import { FlexBox, Icon, IconButton, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { type PropsWithChildren, useCallback, useRef, useState } from 'react';

import { defaultSectionOrder } from 'components/conversation/components/sidebar/conversationSidebar.types';
import { reorderArray } from 'components/conversation/components/sidebar/utils/reorder';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useOrderedDragDrop } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { useSidebarMode } from '../../hooks/useSidebarMode';

import styles from './cardSection.module.scss';

type CardSectionProps = PropsWithChildren & {
  title: string;
  index: number;
  isMinimized?: boolean;
};

const type = Symbol('cardSection');

export const CardSection = ({
  title,
  children,
  index,
  isMinimized,
}: CardSectionProps): JSX.Element => {
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);
  const storage = useServerStorage('user');
  const [isExpanded, setIsExpanded] = useState(true);
  const row = useRef<HTMLDivElement>(null);
  const rowHandle = useRef<HTMLDivElement>(null);

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarSectionOrder = reorderArray(
            storage.data.conversation?.sidebarSectionOrder,
            defaultSectionOrder,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'section',
      });
    },
    [storage],
  );

  const isMoveDisabled = useSidebarMode() === 'visibility';
  const isMoveDisabledRef = useRef(isMoveDisabled);
  isMoveDisabledRef.current = isMoveDisabled;
  const { isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target: row,
    preview: row,
    onMove: handleMove,
    triggerSwap: 'drop',
    canDrag: () => !isMoveDisabledRef.current,
  });

  return (
    <div
      ref={row}
      className={classnames(styles.root, {
        [styles.isOver]: isOver,
        [styles.isMinimized]: isMinimized,
      })}
    >
      {!isMinimized ? (
        <FlexBox alignItems="center" gap="300" padding="300 400 300 300">
          {flags.conversationRightSidebarVisibility && (
            <div
              ref={rowHandle}
              className={classnames(styles.handle, {
                [styles.disabled]: isMoveDisabled,
              })}
            >
              <Icon name="bars" />
            </div>
          )}
          <FlexBox alignItems="center" justifyContent="space-between" flex="1">
            <Text.Paragraph variant="3" subdued padding="100 0">
              {title}
            </Text.Paragraph>
            {flags.conversationRightSidebarVisibility && (
              <FlexBox justifyContent="end" gap="100">
                <IconButton
                  onClick={() => setIsExpanded((prev) => !prev)}
                  icon={isExpanded ? 'angle-down' : 'angle-right'}
                  variant="tertiary"
                />
              </FlexBox>
            )}
          </FlexBox>
        </FlexBox>
      ) : null}
      {isExpanded && children}
    </div>
  );
};
